/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Paper, Stack, Button, Container, Divider, IconButton, Backdrop, CircularProgress, Typography } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect } from "react";

import { useTranslation } from 'react-i18next';
import { toast,} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router-dom';
import { Delete, DragIndicator, Edit } from '@mui/icons-material';
import { Reorder } from 'framer-motion';
import DialogEditFlow from '../components/DialogEditFlow';
import ApproverBusinessLogic from '../../../businessLogic/ApproverBusinessLogic';
import { IApproverFlowGuiModel, IApproverFlowPutGuiModel } from '../../../model/IApproverGuiModel';


const listItemCss = (theme: MaterialTheme) => css({
    border: 1,
    borderRadius: theme.shape.borderRadius,
    bgcolor: theme.palette.background.paper,
    borderColor: theme.palette.secondary.main,
    marginBottom: theme.spacing(1),
    listStyleType: "none",
})
const listGroupCss = () => css({
    margin: 0,
    padding: 0,
})

const FlowApproval = (): JSX.Element => {

    const { organizationId } = useParams();

    const theme = useTheme();

    const { t, i18n } = useTranslation();

    //##########################################################################################################

    const [loader, setLoader] = useState(false);
    const [flows, setFlows] = useState<IApproverFlowGuiModel[]>([]);
    const [flowsError, setFlowsError] = useState<string[]>([]);

    const [chosenFlow, setChosenFlow] = useState<IApproverFlowGuiModel | undefined>(undefined);

    const [openPutDialog, setOpenPutDialog] = useState(false);
    const [openPostDialog, setOpenPostDialog] = useState(false);

    const handleOpenPutDialog = (flow: IApproverFlowGuiModel) => {
        setChosenFlow(flow);
        setOpenPutDialog(true);
    }
    const closeDialog = (reason: "backdropClick" | "escapeKeyDown") => {
        if (reason !== "backdropClick") {
            setOpenPutDialog(false);
            setOpenPostDialog(false);
            getFlows(true);
        }
            
    }
    const handleOpenPostDialog = () => {
        setChosenFlow({
            id: 0,
            organizationId: organizationId ? organizationId : "",
            name: "",
            order: 0,
            autoAssignable: true,
            amountLimit: undefined,
            suppliers: [],
            users: [],
        });
        setOpenPostDialog(true);
    }
    //###############################################################################################################################################
    //###############################################################################################################################################
    //########################################################API Calls and useEffects###############################################################

    const getFlows = async (isMounted: boolean) => {
        if (organizationId) {
            let logic = new ApproverBusinessLogic();
            setLoader(true);
            const result = await logic.getApproverFlows(organizationId);
            setLoader(false);
            if (result.hasErrors) {
                if (isMounted) setFlowsError(result.errors);
            } else {
                if (isMounted) setFlowsError([]);
                if (isMounted) setFlows(result.value.sort((a, b) => a.order - b.order));
            }
        }
    }

    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        
        //Close current window when searching for something new
        getFlows(isMounted);

        return () => { isMounted = false };
    }, []);

    //###############################################################################################################################################
    //###############################################################################################################################################
    const postFlowOrder = async () => {

        let newOrder: number[] = flows.map(e => {
            return e.id
        })

        const logic = new ApproverBusinessLogic();
        setLoader(true);
        const result = await logic.postApproverFlowsReorder(organizationId ? organizationId : "", newOrder);
        setLoader(false);

        if (result.hasErrors) {
            toast(t("approver.errorPutFlow") + result.errors.join(", "));
        } else {
            getFlows(true);
        }
    }
    const deleteFlow = async (id: number) => {

        const logic = new ApproverBusinessLogic();
        setLoader(true);
        const result = await logic.deleteApproverFlow(organizationId ? organizationId : "", id);
        setLoader(false);

        if (result.hasErrors) {
            toast(t("approver.errorDeleteFlow") + result.errors.join(", "));
        } else {
            getFlows(true);
        }
    }
    //########################################################################################
    //########################################################################################

    return <main>
        <Grid container direction="column" wrap="nowrap"
            sx={{
                padding: theme.spacing(2),
                width: "100%",
                height: {
                    //xs: "calc(100vh - " + appBarHeight + "px )",
                    //xl: "calc(100vh)"
                },
                //"& .MuiDataGrid-columnHeaders": { backgroundColor: theme.palette.info.main, color: "white" },
                //"& .MuiDataGrid-footerContainer": { backgroundColor: theme.palette.info.main, color: "white" }
            }}>
            {/*<Grid container spacing={2} padding={theme.spacing(2)}>*/}

            {/*</Grid>*/}
            <Container component={Paper} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Grid container direction="column" spacing={3} paddingTop={theme.spacing(3)} paddingBottom={theme.spacing(3)}>
                    {flowsError.length > 0 ?
                        <Typography>
                            {t("approver.errorWithGettingFlows") + flowsError.join(", ")}
                        </Typography> : <>
                            <Grid>
                                <Button onClick={handleOpenPostDialog} fullWidth variant="contained">
                                    {t("approver.addFlow")}
                                </Button>
                            </Grid>
                            <Grid>
                                <Divider orientation="horizontal" />
                            </Grid>
                            <Grid>
                                <Reorder.Group axis="y" values={flows} onReorder={setFlows} css={listGroupCss}>
                                    {flows.map(e => {
                                        return <Reorder.Item onDragEnd={postFlowOrder} key={e.order} value={e}
                                        css={listItemCss(theme)}>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Stack direction="row" alignItems="center">
                                                    <DragIndicator fontSize="large" />
                                                    <Typography variant="body1">{e.name}</Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={2}>
                                                    <IconButton onClick={() => handleOpenPutDialog(e)}>
                                                        <Edit />
                                                    </IconButton>
                                                    <IconButton onClick={() => deleteFlow(e.id)}>
                                                        <Delete />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                        </Reorder.Item>
                                    })}
                                </Reorder.Group>
                                {chosenFlow ? <>
                                    <DialogEditFlow
                                        organizationId={organizationId}
                                        type="put"
                                        flow={chosenFlow}
                                        open={openPutDialog}
                                        closeDialog={closeDialog} />
                                    <DialogEditFlow
                                        organizationId={organizationId}
                                        type="post"
                                        flow={chosenFlow}
                                        open={openPostDialog}
                                        closeDialog={closeDialog} />
                                </> : <></>}
                            </Grid>
                        </>}
                </Grid>
            </Container>
        </Grid>
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loader}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    </main>

}
export default FlowApproval;